import { DataTable, DataTableMessageRow } from 'components/DataTable'
import { HelpTooltip } from 'components/HelpTooltip'
import {
  banStatusStrings,
  commonStrings,
  organizationManagementStrings,
} from 'constants/strings'
import type { TBanStatusOrganization } from 'types'
import {
  formatBanStatusCount,
  getOrganizationSortLabel,
  isOrganizationBlocked,
  ORGANIZATION_TABLE_SORT_KEYS,
  type OrganizationSort,
  type OrganizationSortKey,
} from './banStatusView'

type OrganizationBreakdownTableProps = {
  organizations: TBanStatusOrganization[]
  loading: boolean
  emptyMessage?: string
  sort: OrganizationSort
  onSort: (key: OrganizationSortKey) => void
}

const headers = banStatusStrings.organizations.headers
type NumberColumnKey = Exclude<
  (typeof ORGANIZATION_TABLE_SORT_KEYS)[number],
  'organizationName'
>
// 見出しと数値セルを同じ順序で生成し、列の追加・並べ替えによるずれを防ぐ。
const numberColumnKeys = ORGANIZATION_TABLE_SORT_KEYS.filter(
  (key): key is NumberColumnKey => key !== 'organizationName',
)
const COLUMN_COUNT = ORGANIZATION_TABLE_SORT_KEYS.length + 1

export function OrganizationBreakdownTable({
  organizations,
  loading,
  emptyMessage = banStatusStrings.organizations.empty,
  sort,
  onSort,
}: OrganizationBreakdownTableProps) {
  let body: JSX.Element | JSX.Element[]

  if (loading) {
    body = (
      <DataTableMessageRow
        colSpan={COLUMN_COUNT}
        message={commonStrings.loading}
      />
    )
  } else if (organizations.length === 0) {
    body = <DataTableMessageRow colSpan={COLUMN_COUNT} message={emptyMessage} />
  } else {
    body = organizations.map((organization) => {
      const blocked = isOrganizationBlocked(organization)
      return (
        <tr
          key={organization.organizationId}
          className={`border-t border-border/60 ${blocked ? 'bg-danger-soft' : 'bg-white'}`}
        >
          <td
            className={`px-2 py-3 text-[11px] font-semibold text-ink sm:text-sm sticky left-0 z-10 ${
              blocked ? 'bg-danger-soft' : 'bg-white'
            } shadow-[2px_0_4px_rgba(0,0,0,0.05)]`}
          >
            {organization.organizationName}
          </td>
          <td className="px-2 py-3 text-[11px] text-ink sm:text-sm">
            {organizationManagementStrings.planLabels[organization.plan]}
          </td>
          {/* 払い出し可能は「0件であること」が異常なので、件数ではなく blocked で強調する */}
          {numberColumnKeys.map((key) => (
            <NumberCell
              key={key}
              value={organization[key]}
              highlight={key === 'allocatable'
                ? blocked
                : (key === 'autoPaused' || key === 'webhookInvalid' || key === 'notAllocatable') &&
                  organization[key] > 0}
            />
          ))}
        </tr>
      )
    })
  }

  return (
    <DataTable
      minWidthClassName="min-w-[1360px]"
      headerRow={
        <tr>
          <SortHeader sortKey="organizationName" sort={sort} onSort={onSort} />
          <th className="px-2 py-3 text-left leading-tight">{headers.plan}</th>
          {numberColumnKeys.map((key) => (
            <SortHeader key={key} sortKey={key} sort={sort} onSort={onSort} />
          ))}
        </tr>
      }
    >
      {body}
    </DataTable>
  )
}

function SortHeader({ sortKey, sort, onSort }: {
  sortKey: OrganizationSortKey
  sort: OrganizationSort
  onSort: (key: OrganizationSortKey) => void
}) {
  const label = getOrganizationSortLabel(sortKey)
  const direction = sort?.key === sortKey ? sort.direction : null
  const help = sortKey !== 'organizationName' ? sortKey : null

  return (
    <th
      scope="col"
      data-testid={`org-sort-header-${sortKey}`}
      aria-sort={direction === null ? 'none' : direction === 'asc' ? 'ascending' : 'descending'}
      className={`px-2 py-3 leading-tight ${sortKey === 'organizationName'
        ? 'text-left sticky left-0 z-10 bg-surface' : 'text-right'} ${
        sortKey === 'pausedOnlyUsers' ? 'w-[168px]' : ''}`}
    >
      <span className="inline-flex max-w-full items-center gap-1 align-middle">
        <button
          type="button"
          aria-label={`${label}${banStatusStrings.organizations.sort.actionSuffix}`}
          onClick={() => onSort(sortKey)}
          className="inline-flex min-w-0 items-center gap-1 rounded text-inherit hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          <span className="min-w-0 whitespace-normal">{label}</span>
          <span aria-hidden="true" className="shrink-0">
            {direction === null ? '↕' : direction === 'asc' ? '↑' : '↓'}
          </span>
        </button>
        {help && (
          <HelpTooltip
            label={`${label}${banStatusStrings.helpLabelSuffix}`}
            description={banStatusStrings.help[help]}
          />
        )}
      </span>
    </th>
  )
}

function NumberCell({ value, highlight }: { value: number | null | undefined; highlight?: boolean }) {
  return (
    <td
      className={`px-2 py-3 text-right text-[11px] tabular-nums sm:text-sm ${
        highlight ? 'font-semibold text-danger' : 'text-ink'
      }`}
    >
      {formatBanStatusCount(value)}
    </td>
  )
}
